import { Dialog, Button, SegmentedControl, IconButton, FormControl, Checkbox } from '@primer/react';
import { CopyIcon } from '@primer/octicons-react';
import { InfoGlyph } from '@upright/ui/common/generic/components/tooltip';
import { Flash } from '@upright/ui/common/generic/components/primer-custom/flash';
import { getMergedIpr } from '@upright/common/model/input/companies/products/product-input-ui/utils';
import { useState, useMemo } from 'react';
import styled from 'styled-components';
import ReactJsonView from '@microlink/react-json-view';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { BlanketProduct } from '@upright/common/model/input/blanket/blanket-product/types';
import { transformToJsx } from '../ipr-towards-upg/transform-to-jsx';
import { iprToLogicalMixinExpression } from '@upright/common/model/input/companies/products/ipr/ipr-to-logical-expressions';
import { logicalMixinExpressionToMixinSyntaxTree } from '@upright/common/model/input/companies/products/ipr/logical-expression-to-mixin-syntax-tree';
import { toExpression } from '@upright/common/model/input/product-mixins';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { MappingServiceProductMatchResult } from '@upright/common/model/input/companies/products';
import { toGracefulLogicalExpression } from '@upright/common/model/input/companies/products/ipr/to-graceful-logical-expression';
import { useGetLatestMixinTemplates } from '../../../../../api/model/input/mixin/get-latest-templates';
import { keyBy } from 'lodash';

type MixinViewTab = 'rendered' | 'syntaxTree' | 'logicalExpression';

// Common interface for both PiuProductState and BlanketProduct
interface DebugProductData {
  title: string;
  normalizedName?: string;
  ipr?: IPRAttributes;
  manualIprEdits?: Partial<IPRAttributes>;
  productMatchResult?: MappingServiceProductMatchResult;
  productCode: string;
}

function toDebugProductData(product: PiuProductState | BlanketProduct): DebugProductData {
  if ('meta' in product) {
    // BlanketProduct
    return {
      title: product.title,
      normalizedName: product.meta?.normalizedName,
      ipr: product.meta?.ipr,
      manualIprEdits: product.meta?.manualIprEdits,
      productMatchResult: product.meta?.productMatchResult,
      productCode: product.meta?.productMatchResult?.productCode ?? 'UNKNOWN',
    };
  }
  // PiuProductState
  const piu = product as PiuProductState;
  return {
    title: piu.title,
    normalizedName: piu.normalizedName,
    ipr: piu.ipr,
    manualIprEdits: piu.manualIprEdits,
    productMatchResult: piu.productMatchResult,
    productCode: piu.productMatchResult?.productCode ?? 'UNKNOWN',
  };
}

interface ModelledAsDialogProps {
  onClose: () => void;
  product: PiuProductState | BlanketProduct;
}

export function ModelledAsDialog({ onClose, product }: ModelledAsDialogProps) {
  const [mixinViewTab, setMixinViewTab] = useState<MixinViewTab>('rendered');
  const [useGraceful, setUseGraceful] = useState(true);
  const data = toDebugProductData(product);
  const title = data.normalizedName || data.title || 'Core product';

  // Fetch mixin templates
  const { data: mixinTemplatesArray } = useGetLatestMixinTemplates();
  const mixinTemplates = useMemo(() => {
    if (!mixinTemplatesArray) return {};
    return keyBy(mixinTemplatesArray, 'expression');
  }, [mixinTemplatesArray]);

  // Get the base logical expression (without graceful handling)
  const baseLogicalExpression = iprToLogicalMixinExpression(
    getMergedIpr({ ipr: data.ipr, manualIprEdits: data.manualIprEdits }),
    title,
    data.productCode,
  );

  // Get graceful expression and unmapped titles
  const { gracefulExpression, unmappedTitles } = toGracefulLogicalExpression(baseLogicalExpression);

  // Choose which expression to display based on the toggle
  const logicalExpression = useGraceful ? gracefulExpression : baseLogicalExpression;

  const syntaxTree = logicalMixinExpressionToMixinSyntaxTree(logicalExpression, mixinTemplates);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const mixinExpression = syntaxTree ? toExpression(syntaxTree) : null;
  return (
    <Dialog returnFocusRef={{ current: null }} onClose={onClose} title="Internal debug view" width="xlarge">
      <Dialog.Body>
        {/*
         * This view is built from getMergedIpr(product's own ipr + own manualIprEdits) only — it does
         * NOT fold in attributes inherited from a parent segment. So the expression below can legitimately
         * omit an inherited attribute that the editor shows on this product and that does reach the
         * published calculation. This banner exists so that gap is not mistaken for a bug (PLT-5816).
         */}
        <NoticeContainer>
          <Flash variant="default" icon={<InfoGlyph />} dismissable={false}>
            <b>Own attributes only.</b> This view shows the attributes set directly on this product. Attributes
            inherited from a parent segment are not shown here and are not included in the expression below, even though
            they do reach the published results.
          </Flash>
        </NoticeContainer>
        <Section>
          <Dialog.Title>Core product matching</Dialog.Title>
          <Fields>
            <FieldValuePair title="Normalized name" value={data.normalizedName} />
            <FieldValuePair title="Matched title" value={data.productMatchResult?.bestChoice} />
            <FieldValuePair title="Matched product code" value={data.productMatchResult?.productCode} />
            <FieldValuePair title="Confidence" value={data.productMatchResult?.confidence} />
          </Fields>
        </Section>
        <Section>
          <Dialog.Title>Transform to regular mixin</Dialog.Title>
          <FormControl>
            <Checkbox
              checked={useGraceful}
              onChange={e => {
                setUseGraceful(e.target.checked);
              }}
            />
            <FormControl.Label>Use graceful expression (handle unmapped titles gracefully)</FormControl.Label>
          </FormControl>
          {unmappedTitles.length > 0 && (
            <UnmappedTitlesWarning>
              <strong>Warning:</strong> The following titles could not be mapped to product codes:{' '}
              {unmappedTitles.join(', ')}
            </UnmappedTitlesWarning>
          )}
          <SegmentedControl
            onChange={index => {
              const tabs: MixinViewTab[] = ['rendered', 'logicalExpression', 'syntaxTree'];
              setMixinViewTab(tabs[index]);
            }}
          >
            <SegmentedControl.Button selected={mixinViewTab === 'rendered'}>Logical expression</SegmentedControl.Button>
            <SegmentedControl.Button selected={mixinViewTab === 'logicalExpression'}>
              Logical expression (JSON)
            </SegmentedControl.Button>
            <SegmentedControl.Button selected={mixinViewTab === 'syntaxTree'}>
              MixinSyntaxTree (JSON)
            </SegmentedControl.Button>
          </SegmentedControl>
          <MixinViewContent>
            {mixinViewTab === 'rendered' && <RenderedMixin>{transformToJsx(logicalExpression, 0)}</RenderedMixin>}
            {mixinViewTab === 'logicalExpression' && (
              <JsonViewWithCopy>
                <CopyButtonContainer>
                  <IconButton
                    icon={CopyIcon}
                    aria-label="Copy to clipboard"
                    size="small"
                    variant="invisible"
                    onClick={() => {
                      void navigator.clipboard.writeText(JSON.stringify(logicalExpression, null, 2));
                    }}
                  />
                </CopyButtonContainer>
                <ReactJsonView src={logicalExpression} collapsed={2} enableClipboard={false} />
              </JsonViewWithCopy>
            )}
            {mixinViewTab === 'syntaxTree' && (
              <JsonViewWithCopy>
                <CopyButtonContainer>
                  <IconButton
                    icon={CopyIcon}
                    aria-label="Copy to clipboard"
                    size="small"
                    variant="invisible"
                    onClick={() => {
                      void navigator.clipboard.writeText(JSON.stringify(syntaxTree, null, 2));
                    }}
                  />
                </CopyButtonContainer>
                <ReactJsonView src={syntaxTree} collapsed={2} enableClipboard={false} />
              </JsonViewWithCopy>
            )}
          </MixinViewContent>
        </Section>
        <Section>
          <Dialog.Title>Transform to regular mixin expression</Dialog.Title>
          <ExpressionCode>{mixinExpression ?? 'N/A'}</ExpressionCode>
        </Section>
        <Section>
          <Dialog.Title>Raw vector match result</Dialog.Title>
          <pre>{JSON.stringify(data.productMatchResult?.top_k, null, 2)}</pre>
        </Section>
      </Dialog.Body>
      <Dialog.Footer>
        <FooterContainer>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </FooterContainer>
      </Dialog.Footer>
    </Dialog>
  );
}

interface FieldValuePairProps {
  title: React.ReactNode;
  value: React.ReactNode | undefined;
}

function FieldValuePair({ title: field, value }: FieldValuePairProps) {
  return (
    <FieldValuePairContainer>
      <Field>{field}</Field>
      <Value>{value ? value : 'N/A'}</Value>
    </FieldValuePairContainer>
  );
}

const NoticeContainer = styled.div`
  margin-bottom: var(--space__lg);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);

  & + & {
    margin-top: var(--space__lg);
  }
`;

const FieldValuePairContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
`;

const Field = styled.div`
  font-weight: 500;
  color: var(--fgColor-default);
  min-width: 12em;
`;

const Value = styled.div`
  font-weight: 400;
  color: var(--fgColor-default);
`;

const RenderedMixin = styled.div``;

const MixinViewContent = styled.div`
  margin-top: var(--space__sm);
`;

const ExpressionCode = styled.code`
  display: block;
  padding: var(--space__sm);
  background-color: var(--bgColor-muted);
  border-radius: var(--border-radius__md);
  font-family: var(--fontStack-monospace);
  white-space: pre-wrap;
  word-break: break-all;
`;

const JsonViewWithCopy = styled.div`
  position: relative;
`;

const CopyButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
`;

const Fields = styled.div`
  font-size: var(--font-size__md);
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const UnmappedTitlesWarning = styled.div`
  padding: var(--space__sm);
  background-color: var(--bgColor-attention-muted);
  border: 1px solid var(--borderColor-attention-muted);
  border-radius: var(--border-radius__md);
  color: var(--fgColor-attention);
  font-size: var(--font-size__md);
`;
